import {
  atsSyncedFieldHelp,
  isAtsManagedField,
} from '@/features/jobs/atsManagedFields';
import { tCatalog } from '@/i18n/tCatalog';
import {
  buildCommonValidationMessages,
  isZeroNumberValue,
  isUnsetNumberValue,
  optionalNumberField,
  optionalString,
} from '@/utils/validation/schemaHelpers';

import type { FieldDef } from './types';
import type {
  EmploymentType,
  EmploymentTypeField,
} from '@/types/employmentType';
import type { DefaultOptionType } from 'antd/es/select';
import type { TFunction } from 'i18next';
import type { RefinementCtx } from 'zod';

type EmploymentTypeDisplayContext =
  'candidate' | 'introduction' | 'job' | 'offer';

function getNestedValue(
  values: Record<string, unknown>,
  path: (string | number)[],
): unknown {
  let current: unknown = values;
  for (const segment of path) {
    if (current == null || typeof current !== 'object') {
      return undefined;
    }
    current = (current as Record<string, unknown>)[String(segment)];
  }
  return current;
}

function isFieldVisibleForContext(
  field: EmploymentTypeField,
  displayContext: EmploymentTypeDisplayContext,
): boolean {
  if (displayContext === 'candidate') {
    return field.is_applicable_to_candidates;
  }
  if (displayContext === 'introduction') {
    return field.is_applicable_to_introductions;
  }
  if (displayContext === 'job') {
    return field.is_applicable_to_jobs;
  }
  return field.is_applicable_to_offers;
}

function employmentTypeHidden(
  basePath: (string | number)[],
  employmentTypeId: string,
) {
  return (values: Record<string, unknown>) =>
    getNestedValue(values, [...basePath, 'type', 'id']) !== employmentTypeId;
}

function buildDynamicEmploymentFieldDef(
  t: TFunction,
  basePath: (string | number)[],
  employmentTypeId: string,
  field: EmploymentTypeField,
  currencyReplaceToken: (value: unknown) => unknown,
  displayContext: EmploymentTypeDisplayContext,
  options: {
    isAtsConnected?: boolean;
    omitSecondaryLabels?: boolean;
  } = {},
): FieldDef {
  const { isAtsConnected } = options;
  const messages = buildCommonValidationMessages(t);
  const namePrefix = [
    ...basePath,
    'values',
    employmentTypeId,
    'field',
    field.id,
  ];
  const secondaryLabel =
    options.omitSecondaryLabels || !field.secondary_label
      ? ''
      : String(currencyReplaceToken(t(field.secondary_label)));
  const label = `${String(t(field.label))}${secondaryLabel ? ` (${secondaryLabel})` : ''}`;
  const showRequired = false;
  const disabled = Boolean(isAtsConnected && isAtsManagedField(field.id));
  const help = atsSyncedFieldHelp(disabled, t);

  if (field.is_multiline) {
    return {
      name: [...namePrefix, 'text_value'],
      validation: optionalString(),
      ui: {
        type: 'textarea',
        label: t(field.label),
        help,
        hidden: employmentTypeHidden(basePath, employmentTypeId),
        required: showRequired,
        componentProps: { autoSize: { minRows: 3 }, disabled },
      },
    };
  }

  if (field.is_numeric || field.is_decimal) {
    return {
      name: [...namePrefix, 'numeric_value'],
      validation: optionalNumberField(messages),
      ui: {
        type: 'number',
        label,
        help,
        colSpan: 12,
        hidden: employmentTypeHidden(basePath, employmentTypeId),
        required: showRequired,
        componentProps: {
          step: field.is_decimal ? 0.01 : 1,
          disabled,
        },
      },
    };
  }

  if (field.is_select) {
    return {
      name: [...namePrefix, 'text_value'],
      validation: optionalString(),
      ui: {
        type: 'select',
        label,
        help,
        colSpan: 12,
        hidden: employmentTypeHidden(basePath, employmentTypeId),
        required: showRequired,
        componentProps: { disabled },
        options:
          field.select_fields?.split(',').map((value) => {
            const trimmedValue = value.trim();
            return {
              value: trimmedValue,
              label: tCatalog('other', trimmedValue),
            };
          }) ?? [],
      },
    };
  }

  return {
    name: [...namePrefix, 'text_value'],
    validation: optionalString(),
    ui: {
      type: 'text',
      label,
      help,
      colSpan: 12,
      hidden: employmentTypeHidden(basePath, employmentTypeId),
      required: showRequired,
      componentProps: { disabled },
    },
  };
}

type BuildEmploymentTypeFieldsOptions = {
  t: TFunction;
  basePath?: (string | number)[];
  employmentTypes: EmploymentType[];
  displayContext: EmploymentTypeDisplayContext;
  employmentTypeOptions: DefaultOptionType[];
  onEmploymentTypeChange?: (typeId: string) => void;
  selectDataTestId?: string;
  selectDropdownClassName?: string;
  currencyReplaceToken?: (value: unknown) => unknown;
  isAtsConnected?: boolean;
  hideEmploymentTypeSelect?: boolean;
  omitSecondaryLabels?: boolean;
};

export function buildEmploymentTypeFieldDefs(
  options: BuildEmploymentTypeFieldsOptions,
): FieldDef[] {
  const {
    t,
    basePath = [],
    employmentTypes,
    displayContext,
    employmentTypeOptions,
  } = options;
  const replaceToken =
    options.currencyReplaceToken ?? ((value: unknown) => value);

  const typeSelectDef: FieldDef = options.hideEmploymentTypeSelect
    ? {
        name: [...basePath, 'type', 'id'],
        validation: optionalString(),
        ui: {
          type: 'hidden',
        },
      }
    : {
        name: [...basePath, 'type', 'id'],
        validation: optionalString(),
        ui: {
          type: 'select',
          label: t('TYPE_OF_EMPLOYMENT'),
          required: false,
          componentProps: {
            showSearch: { optionFilterProp: 'label', filterOption: true },
            defaultActiveFirstOption: true,
            disabled: Boolean(options.isAtsConnected),
            options: employmentTypeOptions,
            onChange: (value: string) => {
              options.onEmploymentTypeChange?.(value ? String(value) : '');
            },
            ...(options.selectDataTestId
              ? { 'data-testid': options.selectDataTestId }
              : {}),
            classNames: options.selectDropdownClassName
              ? { popup: { root: options.selectDropdownClassName } }
              : undefined,
          },
        },
      };

  const defs: FieldDef[] = [typeSelectDef];

  for (const employmentType of employmentTypes) {
    const visibleFields = employmentType.fields.filter((field) =>
      isFieldVisibleForContext(field, displayContext),
    );
    for (const field of visibleFields) {
      defs.push(
        buildDynamicEmploymentFieldDef(
          t,
          basePath,
          employmentType.id,
          field,
          replaceToken,
          displayContext,
          {
            isAtsConnected: options.isAtsConnected,
            omitSecondaryLabels: options.omitSecondaryLabels,
          },
        ),
      );
    }
  }

  return defs;
}

function stringifyPrimitiveId(value: unknown): string {
  if (typeof value === 'string') {
    return value;
  }
  if (typeof value === 'number') {
    return String(value);
  }
  return '';
}

function stringifyTextValue(value: unknown): string {
  if (typeof value === 'string') {
    return value;
  }
  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  return '';
}

function isEmptyFieldValue(
  field: EmploymentTypeField,
  fieldData: Record<string, unknown> | undefined,
  required: boolean,
): boolean {
  if (field.is_numeric || field.is_decimal) {
    const value = fieldData?.numeric_value;
    if (isUnsetNumberValue(value)) {
      return true;
    }
    if (required && isZeroNumberValue(value)) {
      return true;
    }
    return false;
  }
  const value = fieldData?.text_value;
  return value == null || stringifyTextValue(value).trim() === '';
}

function validateEmploymentTypeRecordRefine(
  itemRecord: Record<string, unknown>,
  ctx: RefinementCtx,
  employmentTypes: EmploymentType[],
  displayContext: EmploymentTypeDisplayContext,
  requiredMessage: string,
  pathPrefix: (string | number)[],
): void {
  const typeId = getNestedValue(itemRecord, ['type', 'id']);
  const typeIdStr = stringifyPrimitiveId(typeId);
  if (typeIdStr.trim() === '') {
    ctx.addIssue({
      code: 'custom',
      path: [...pathPrefix, 'type', 'id'],
      message: requiredMessage,
    });
    return;
  }

  const employmentType = employmentTypes.find(
    (entry) => entry.id === typeIdStr,
  );
  if (!employmentType) {
    return;
  }

  if (displayContext === 'candidate') {
    return;
  }

  const visibleFields = employmentType.fields.filter((field) =>
    isFieldVisibleForContext(field, displayContext),
  );

  for (const field of visibleFields) {
    if (field.is_optional) {
      continue;
    }

    const valuePath = ['values', typeIdStr, 'field', field.id];
    const fieldData = getNestedValue(itemRecord, valuePath) as
      Record<string, unknown> | undefined;

    if (isEmptyFieldValue(field, fieldData, true)) {
      const valueKey =
        field.is_numeric || field.is_decimal ? 'numeric_value' : 'text_value';
      ctx.addIssue({
        code: 'custom',
        path: [...pathPrefix, ...valuePath, valueKey],
        message: requiredMessage,
      });
    }
  }
}

export function validateEmploymentTypeRefine(
  data: Record<string, unknown>,
  ctx: RefinementCtx,
  employmentTypes: EmploymentType[],
  displayContext: EmploymentTypeDisplayContext,
  requiredMessage: string,
  objectPathPrefix: (string | number)[] = ['employment_type'],
): void {
  const item = getNestedValue(data, objectPathPrefix);
  if (item == null || typeof item !== 'object') {
    ctx.addIssue({
      code: 'custom',
      path: [...objectPathPrefix, 'type', 'id'],
      message: requiredMessage,
    });
    return;
  }

  validateEmploymentTypeRecordRefine(
    item as Record<string, unknown>,
    ctx,
    employmentTypes,
    displayContext,
    requiredMessage,
    objectPathPrefix,
  );
}

export function validateEmploymentTypesRefine(
  data: Record<string, unknown>,
  ctx: RefinementCtx,
  employmentTypes: EmploymentType[],
  displayContext: EmploymentTypeDisplayContext,
  requiredMessage: string,
  listPathPrefix: (string | number)[] = ['employment_types'],
): void {
  const items = data.employment_types;
  if (!Array.isArray(items) || items.length === 0) {
    ctx.addIssue({
      code: 'custom',
      path: listPathPrefix,
      message: requiredMessage,
    });
    return;
  }

  items.forEach((item, index) => {
    if (item == null || typeof item !== 'object') {
      return;
    }
    validateEmploymentTypeRecordRefine(
      item as Record<string, unknown>,
      ctx,
      employmentTypes,
      displayContext,
      requiredMessage,
      [...listPathPrefix, index],
    );
  });
}
